import React from 'react';
// Redux
import { useSelector } from 'react-redux';
// Selectors
import { selectStapleMode, selectStapleById, selectPageDuplicateInWorkspace } from 'app/store/page/page.selectors';

import { selectIsPageAdditional } from 'app/store/DMSDocumentPages/DMSDocumentPages.selectors';
// Dialogs
import CardPageDateDialog from 'app/dialogs/CardPageDateDialog';
import CardPageTagsDialog from 'app/dialogs/CardPageTagsDialog';
// Components
import { Box, Tooltip, Chip } from '@mui/material';
// Selectors
import { selectWorkspaceType } from 'app/store/ui/uiSelectors';
import { selectEpisodeEntity } from 'app/store/Episodes/Episodes.selectors';
import { selectHasPageAllEpisodesAnyNotes } from 'app/store/Episodes/Episodes.selectors';
// Utilities
import useCaseStatus from 'app/hooks/useCaseStatus';
import useToggle from 'app/hooks/useToggle';

// Components
import Icon from 'app/components/Icon';

import useFormattedDate from 'app/hooks/useFormattedDate';
import useFormattedTime from 'app/hooks/useFormattedTime';
import { selectTrialMode } from 'app/store/AppUI/AppUI.selectors';
// i18next
import { useTranslation } from 'react-i18next';

import PageCompareDialog from '@root/dialogs/PageCompareDialog';

const CardHeader = ({
  // Props
  page, ...props
}) => {
  const { t } = useTranslation();

  const stapleId = page.staple && page.staple.id ? page.staple.id : null;

  const { documentId, pageNum, staple = null } = page;
  const { episodeId, index, isFilesPage, isWorkspacePage, isSearchPage } = props;

  // State
  const trialMode = useSelector(selectTrialMode);
  const workspaceType = useSelector(selectWorkspaceType);
  const staplePages = useSelector((state) => selectStapleById(state, { stapleId }));
  const stapleMode = useSelector(selectStapleMode);
  // ToDO
  const isPageAdditional = useSelector(state => selectIsPageAdditional(state, { documentId }));

  const episodeEntity = useSelector((state) => selectEpisodeEntity(state, { episodeId }));
  const parentEpisodeEntity = useSelector((state) => selectEpisodeEntity(state, { episodeId: page.parentEpisodeId }));
  const hasNotes = useSelector((state) => selectHasPageAllEpisodesAnyNotes(state, { documentId, pageNum, stapleId: staple?.id }));
  const pageDuplicate = useSelector((state) => selectPageDuplicateInWorkspace(state, { documentId: page.documentId, pageNum: page.pageNum }));

  const { isArchived } = useCaseStatus();

  const formattedParentDate = useFormattedDate(parentEpisodeEntity?.date);
  const formattedParentTime = useFormattedTime(parentEpisodeEntity?.time);

  const formattedDate = useFormattedDate(episodeEntity?.date);
  const formattedTime = useFormattedTime(episodeEntity?.time);

  const { open, toggle } = useToggle();
  const { open:openCardPageDateDialog, toggle:toggleCardPageDateDialog } = useToggle();

  const [ dialogCompareOpen, setDialogCompareOpen ] = React.useState(false);

  const toggleDialogCompare = () => {
    if ( trialMode ) return;
    setDialogCompareOpen((prevState) => !prevState);
  }

  const renderPageNum = (
    <Box
      sx={{
        minWidth: 24,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        bgcolor: 'white',
        fontSize: '1rem',
        fontWeight: 500,
        py: 0.5,
        px: 1.5,
        textAlign: 'center'
      }}
    >{typeof index !== 'undefined' ? index + 1 : pageNum}</Box>
  );

  const renderDateText = () => {
    if ( episodeEntity ) {
      if ( !formattedDate ) return null;
      return [formattedDate, formattedTime].filter(Boolean).join(' ');
    } else {
      if ( !formattedParentDate && !props.disableBodyClick ) return t('components.cardPage.setDate');
      return [formattedParentDate, formattedParentTime].filter(Boolean).join(' ');
    }
  }

  const renderPageDate = () => {
    if ( staple && staple.order > 1 ) return null;
    const dateText = renderDateText();
    if ( !dateText ) return null;
    return (
      <Box
        sx={{
          color: 'black',
          fontSize: '1rem',
          fontWeight: 500
        }}
        onClick={() => {
          if ( trialMode || stapleMode || isArchived || episodeEntity || props.disableBodyClick ) return false;
          toggleCardPageDateDialog();
        }}
      >{renderDateText()}</Box>
    )
  }

  const renderPageTag = () => {
    if ( (staple && staple.order > 1) || trialMode ) return null;
    if ( (episodeEntity || props.disableBodyClick) && !page.tag ) return null;
    return (
      <Box>
        <Chip
          sx={{
            color: !page.tag ? 'rgba(0,0,0,0.54)' : 'initial',
            fontSize: '0.875rem',
            fontWeight: 500
          }}
          label={page.tag || t('components.cardPage.setTag')}
          size="small"
          onClick={() => {
            if ( stapleMode || isArchived || episodeEntity || props.disableBodyClick ) return false;
            toggle();
          }}
        />
      </Box>
    )
  }

  return (
    <React.Fragment>
      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            {renderPageNum}
            {renderPageTag()}
          </Box>
          {renderPageDate()}
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
          {workspaceType === 'pages' && page.staple ? (
            <Tooltip
              title={`${t('components.cardPage.pagesInStaple', { pages: staplePages?.length || 0 })}`}
              aria-label={undefined}
            ><Box sx={{ color: 'rgba(0,0,0,0.54)' }}><Icon icon="stapled" size="small" /></Box></Tooltip>
          ) : null}
          {!trialMode ? (
            <React.Fragment>
              {isPageAdditional && isWorkspacePage ? (
                <Tooltip title={t('labels.additional')} aria-label={undefined}>
                  <Box sx={{ color: 'rgba(0,0,0,0.54)' }}><Icon icon="additional" size="small" /></Box>
                </Tooltip>
              ) : null}
              {hasNotes ? (
                <Tooltip title={t('labels.notes')} aria-label={undefined}>
                  <Box sx={{ color: 'rgba(0,0,0,0.54)' }}><Icon icon="notes" size="small" /></Box>
                </Tooltip>
              ) : null}
              {((isFilesPage || isSearchPage) && pageDuplicate) ? (
                <Tooltip title={t('components.cardPage.showDuplicates')} aria-label={undefined}>
                  <Box sx={{ color: 'rgba(0,0,0,0.54)' }} onClick={toggleDialogCompare}><Icon icon="columns_2" size="small" /></Box>
                </Tooltip>
              ) : null}
            </React.Fragment>
          ) : null}
        </Box>
      </Box>
      {/* Page tag dialog */}
      {open ? (
        <CardPageTagsDialog
          open={open}
          onClose={toggle}
          page={page}
        />
      ) : null}
      {openCardPageDateDialog ? (
        <CardPageDateDialog
          open={openCardPageDateDialog}
          onClose={toggleCardPageDateDialog}
          page={page}
        />
      ) : null}
      {dialogCompareOpen ? (
        <PageCompareDialog
          open={dialogCompareOpen}
          onClose={toggleDialogCompare}
          page={page}
          pageDuplicate={pageDuplicate}
        />
      ) : null}
    </React.Fragment>
  )
}

export default CardHeader;
